import { auth } from '../firebase';

const API_BASE = import.meta.env.VITE_API_BASE as string;

async function authHeaders(): Promise<Record<string, string>> {
  const user = auth.currentUser;
  if (!user) throw new Error('Not authenticated');
  const token = await user.getIdToken();
  return { Authorization: `Bearer ${token}` };
}

async function readError(res: Response): Promise<string> {
  try {
    const body = (await res.json()) as { error?: string };
    return body.error || `HTTP ${res.status}`;
  } catch {
    return `HTTP ${res.status}`;
  }
}

export async function apiGet<T>(path: string): Promise<T> {
  const res = await fetch(`${API_BASE}${path}`, {
    headers: await authHeaders(),
  });
  if (!res.ok) throw new Error(await readError(res));
  return (await res.json()) as T;
}

export async function apiJson<T>(
  method: 'POST' | 'PUT' | 'PATCH' | 'DELETE',
  path: string,
  body?: unknown,
  opts?: { extraHeaders?: Record<string, string> },
): Promise<T> {
  const headers: Record<string, string> = {
    ...(await authHeaders()),
    'Content-Type': 'application/json',
    ...(opts?.extraHeaders ?? {}),
  };
  const res = await fetch(`${API_BASE}${path}`, {
    method,
    headers,
    body: body ? JSON.stringify(body) : undefined,
  });
  if (!res.ok) throw new Error(await readError(res));
  return (await res.json()) as T;
}

/**
 * Authenticated GET that returns the full Response object — used for blob
 * downloads (e.g. ZIPs) where we need headers / streaming access rather
 * than a pre-parsed JSON body.
 */
export async function apiGetRaw(path: string): Promise<Response> {
  const res = await fetch(`${API_BASE}${path}`, {
    headers: await authHeaders(),
  });
  if (!res.ok) throw new Error(await readError(res));
  return res;
}

/**
 * Stage 27: authenticated fetch that returns the full Response object for
 * arbitrary methods + bodies. Used by the selection-driven ZIP downloads
 * where we POST `{ ids }` and stream back a ZIP blob. Unlike `apiJson`,
 * this does NOT JSON-decode the response — the caller reads `.blob()` or
 * inspects headers directly. Does NOT throw on non-OK status so callers
 * can inspect the response body for structured error details.
 */
export async function apiFetchRaw(
  path: string,
  init?: RequestInit,
): Promise<Response> {
  const headers = new Headers(init?.headers);
  const auth = await authHeaders();
  for (const [k, v] of Object.entries(auth)) headers.set(k, v);
  return fetch(`${API_BASE}${path}`, { ...init, headers });
}

export async function apiMultipart<T>(
  path: string,
  formData: FormData,
): Promise<T> {
  const res = await fetch(`${API_BASE}${path}`, {
    method: 'POST',
    headers: await authHeaders(),
    body: formData,
  });
  if (!res.ok) throw new Error(await readError(res));
  return (await res.json()) as T;
}
